import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import useResultStyles from '../../styles/perfectMatch/result';
import { fetchNextPerfectMatchCode, setResultType } from '../../features/quizCode/perfectMatchSlice';
import { RootState, AppDispatch } from '../../stores/store';

import goatImage from '../../assets/images/perfect_match/tap.png';
import womanImage from '../../assets/images/perfect_match/tipa.png';
import manImage from '../../assets/images/perfect_match/tip.png';
import logoText from '../../assets/images/perfect_match/logo.jpg';
import breweryImage from '../../assets/images/perfect_match/background.jpg';
import promoalcool from '../../assets/images/promo-despre-alcool.png';

interface ResultProps {
    shop: 'kaufland' | 'carrefour' | 'mega-image';
    answers: string[];
    onRestart?: () => void;
}

const resultTypes = {
    type1: {
        title: 'Bere Blondă? Bere Brună? Ne-aduc Mereu Împreună!',
        description: 'Lejer, lipsit de dramă, easy-going. În relații, ești mai curând genul care se bucură de momente simple și necomplicate.\n\nFaci pereche perfectă cu Kozel Premium Lager! Daaar.. surpriză… te potrivești la fel de bine și cu Kozel Dark.'
    },
    type2: {
        title: 'Bere Blondă? Bere Brună? Ne stă bine Împreună!',
        description: 'Surprinzător și misterios. Relațiile tale nu sunt monotone sau pline de rutină. Lași un impact puternic asupra celor din jur și te faci plăcut de toaaaată lumea.\n\nFaci pereche perfectă cu Kozel Dark! Daaar.. surpriză… te potrivești la fel de bine și cu Kozel Premium Lager.'
    }
} as const;

const calculateResultType = (answers: string[]): 'type1' | 'type2' => {
    // Numără răspunsurile de tip 1 (A și C) și tip 2 (B)
    let type1Count = 0;
    let type2Count = 0;

    answers.forEach(answer => {
        if (answer === 'A' || answer === 'C') {
            type1Count++;
        } else if (answer === 'B') {
            type2Count++;
        }
    });

    // Returnează tipul cu cele mai multe răspunsuri
    // Dacă sunt egale, returnăm type1 ca fallback
    return type1Count >= type2Count ? 'type1' : 'type2';
};

const Result: React.FC<ResultProps> = ({ shop, answers, onRestart }) => {
    const classes = useResultStyles();
    const dispatch = useDispatch<AppDispatch>();
    const { code, loading, resultType } = useSelector((state: RootState) => state.perfectMatch);

    const hasFetchedCode = useRef(false);

    useEffect(() => {
        // Calculează tipul de rezultat bazat pe răspunsuri
        const calculatedType = calculateResultType(answers);
        dispatch(setResultType(calculatedType));

        if (shop === 'carrefour' && !hasFetchedCode.current) {
            hasFetchedCode.current = true;
            dispatch(fetchNextPerfectMatchCode());
        }
    }, [dispatch, shop, answers]);

    const currentResult = resultType ? resultTypes[resultType] : resultTypes.type1;
    const showCode = shop === 'carrefour';

    return (
        <Box className={classes.container}>
            <Box className={classes.backgroundImage}>
                <img
                    src={breweryImage}
                    alt="Brewery background"
                    className={classes.breweryImage}
                />
            </Box>

            <Box className={classes.contentOverlay}>
                <Box className={classes.headerSection}>
                    <img
                        src={logoText}
                        alt="Kozel Perfect Match"
                        className={classes.logoImage}
                    />
                </Box>

                <Box className={classes.resultSection}>
                    <h2 className={classes.resultTitle} style={{ marginBottom: showCode ? '0.3rem' : '1.5rem' }}>
                        TE POTRIVEȘTI DE MINUNE CU ...
                    </h2>

                    <p className={classes.resultText} style={{ marginBottom: showCode ? '0.3rem' : '2rem' }}>
                        {currentResult.description}
                    </p>

                    {showCode && (
                        <>
                            <div className={classes.resultTitle} style={{ marginTop: '0.3rem', marginBottom: '0.3rem' }}>
                                30% DISCOUNT
                            </div>

                            {loading ? (
                                <Box className={classes.codeContainer}>
                                    <div className={classes.codeLabel}>
                                        Se încarcă codul...
                                    </div>
                                </Box>
                            ) : code ? (
                                <>
                                    <div className={classes.codeLabel}>
                                        CODUL TĂU DE REDUCERE:
                                    </div>
                                    <Box className={classes.codeContainer}>
                                        <div className={classes.codeValue}>
                                            {code}
                                        </div>
                                    </Box>
                                    <p className={classes.saveCodeText}>
                                        Salvează acest cod! Îl vei putea folosi pentru reducerea ta.
                                    </p>
                                </>
                            ) : (
                                <Box className={classes.codeContainer}>
                                    <div className={classes.codeLabel}>
                                        Nu mai sunt coduri disponibile
                                    </div>
                                </Box>
                            )}
                        </>
                    )}

                    <h3 className={classes.resultTitle} style={{ marginTop: showCode ? '0.3rem' : '2rem', marginBottom: '1rem' }}>
                        {currentResult.title}
                    </h3>

                    <button
                        className={classes.finalizeButton}
                        onClick={onRestart}
                    >
                        Finalizează
                    </button>
                    <img
                        src={promoalcool}
                        alt="Promo Alcool"
                        className={classes.promoalcool}
                    />
                </Box>

                <Box className={classes.charactersSection}>
                    <img
                        src={womanImage}
                        alt="Woman with beer"
                        className={classes.characterLeft}
                    />
                    <img
                        src={goatImage}
                        alt="Kozel goat"
                        className={classes.characterCenter}
                    />
                    <img
                        src={manImage}
                        alt="Man with beer"
                        className={classes.characterRight}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default Result;